import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables - check deployment configuration');
}

// Custom lock for @supabase/supabase-js auth. The default uses
// navigator.locks with no timeout, so a stale lock from another tab (or a
// crashed/extension-held lock) makes every supabase call wait indefinitely.
//
// Strategy: use `ifAvailable: true`, which tells the browser to grant the
// lock only if it's free at the moment of the request. If another tab holds
// it, the callback gets `null` immediately and we fall through to running
// the work without the lock. Supabase auth ops are idempotent (token
// refresh races resolve to the same valid token at worst), so worst case
// is a duplicated refresh on token expiry, which is invisible to the user.
//
// What this fixes: previously, on page load, ~5 useEffects each made a
// supabase call. If a stale cross-tab lock was held, each call waited up
// to 15 s before falling through — compounding to ~75 s of pure lock
// waits before any data loaded. With `ifAvailable: true`, a contended
// lock costs ~0 ms instead of 15 s. We retain a small bounded wait
// (`acquireTimeout` clamp) for cases where the lock is briefly held by
// our own tab's auto-refresh path, so we don't unnecessarily skip the
// lock for legitimate same-tab queueing.
const lockWithTimeout = async <R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>
): Promise<R> => {
  if (typeof navigator === 'undefined' || !('locks' in navigator) || !navigator.locks) {
    return await fn();
  }
  // Brief wait for our own tab to finish a queued auth op, then fall
  // through. 500 ms is enough for normal in-tab serialization; longer
  // waits are almost always a stale cross-tab lock.
  const timeoutMs = acquireTimeout > 0 && acquireTimeout < 500 ? acquireTimeout : 500;

  // Try to grab the lock if it's immediately available.
  const fastResult = await navigator.locks.request(
    name,
    { mode: 'exclusive', ifAvailable: true },
    async (lock) => {
      if (lock === null) {
        return { acquired: false } as const;
      }
      return { acquired: true, value: await fn() } as const;
    }
  );
  if (fastResult.acquired) {
    return fastResult.value;
  }

  // Lock was contended. Wait briefly in case it's the same tab's own
  // auto-refresh; if that doesn't clear, run without the lock.
  const abortCtrl = new AbortController();
  const timer = setTimeout(() => abortCtrl.abort(), timeoutMs);
  try {
    return await navigator.locks.request(
      name,
      { mode: 'exclusive', signal: abortCtrl.signal },
      async () => await fn()
    );
  } catch (err: any) {
    if (abortCtrl.signal.aborted) {
      console.debug(
        `[supabase-lock] lock "${name}" contended for >${timeoutMs}ms; proceeding without lock`
      );
      return await fn();
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
};

let supabase: any;

try {
  supabase = createClient(supabaseUrl, supabaseKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      lock: lockWithTimeout
    }
  });
} catch {
  // Fallback: try with server-safe config (no persistSession in non-browser envs)
  try {
    const clientConfig: any = {
      auth: {
        autoRefreshToken: typeof window !== 'undefined',
        persistSession: typeof window !== 'undefined',
        detectSessionInUrl: true,
        lock: lockWithTimeout
      }
    };
    supabase = createClient(supabaseUrl, supabaseKey, clientConfig);
  } catch {
    // Last resort: bare minimum
    supabase = createClient(supabaseUrl, supabaseKey);
  }
}

// Global auth state change handler (only in browser environment)
if (typeof window !== 'undefined') {
  supabase.auth.onAuthStateChange((event: string, session: any) => {
    // Auth state changed - no logging
  });
}

export { supabase };

// Storage bucket name
export const STORAGE_BUCKET = 'documents';